import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Edit,
  Loader2,
  ShieldCheck,
  GraduationCap,
  Calendar,
  Languages,
  BookOpen,
  User,
  FileCheck2,
  Home as HomeIcon,
  UserPlus,
  Check,
  Circle,
  ChevronRight,
  Search as SearchIcon,
  MapPin,
  Sparkles,
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import TrustScoreRing from '../components/TrustScoreRing';
import { useProfile } from '../contexts/ProfileContext';

const API = process.env.REACT_APP_BACKEND_URL;

const YEAR_LABELS = {
  just_starting: 'Just starting',
  year_1: 'Year 1',
  year_2: 'Year 2',
  year_3: 'Year 3',
  year_4_plus: 'Year 4+',
  graduate: 'Graduate / Postgraduate',
};

const PROMPT_LABELS = {
  bio_prompt_studying: 'Studying',
  bio_prompt_routine: 'Daily routine',
  bio_prompt_freetime: 'Free time',
  bio_prompt_roommate: 'As a roommate',
};

const TIER_LABELS = {
  getting_started: 'Getting started',
  verified_student: 'Verified student',
  trusted_student: 'Trusted student',
};

// Each backend Trust Score component → the on-site action that satisfies it.
// Actions that aren't yet implementable (Stripe Identity, account age) return
// null — we render them as read-only rows.
const TRUST_ACTION = {
  identity_verified: { to: '/settings', label: 'Verify with Stripe Identity (coming soon)', disabled: true },
  email_verified: { to: '/profile/edit#personal', label: 'Go to Personal' },
  phone_verified: { to: '/profile/edit#personal', label: 'Add and verify phone' },
  acceptance_letter: { to: '/profile/documents', label: 'Upload acceptance letter' },
  gov_id: { to: '/profile/documents', label: 'Upload government ID' },
  study_permit: { to: '/profile/documents', label: 'Upload study permit' },
  proof_of_funds: { to: '/profile/documents', label: 'Upload proof of funds' },
  reference: { to: '/profile/edit#references', label: 'Add a reference' },
  profile_80: { to: '/profile/edit#about', label: 'Finish your profile' },
  tagline_bio: { to: '/profile/edit#about', label: 'Write a tagline and bio' },
  account_age: null, // time-based, nothing to click
};

const TABS = [
  { key: 'about', label: 'About me', icon: User },
  { key: 'applications', label: 'Rental applications', icon: FileCheck2 },
  { key: 'roommates', label: 'Roommates', icon: UserPlus },
];

/* ─────────────────────────────── About Me tab ─────────────────────────────── */

const IdentityCard = ({ u, photoUrl, initial, identityVerified }) => (
  <Card className="bg-card-cream border-amber-200/60 shadow-soft" data-testid="identity-card">
    <CardContent className="p-5 sm:p-6 text-center">
      <div
        className="mx-auto h-40 w-40 sm:h-48 sm:w-48 rounded-full overflow-hidden bg-amber-500 flex items-center justify-center shadow-soft border-4 border-white"
        data-testid="profile-photo-display"
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-white font-heading font-bold text-5xl sm:text-6xl">{initial}</span>
        )}
      </div>
      <h2 className="mt-4 font-heading font-extrabold text-xl sm:text-2xl text-slate-900 leading-tight">
        {u.first_name} {u.name?.split(' ').slice(1).join(' ') || ''}
      </h2>
      {u.country_of_origin && u.show_country_on_profile && (
        <p className="mt-1 text-sm text-slate-600 inline-flex items-center gap-1.5 justify-center">
          <MapPin className="h-3.5 w-3.5 text-slate-400" />
          From {u.country_of_origin}
        </p>
      )}
      {identityVerified && (
        <p
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full"
          data-testid="identity-verified-badge"
        >
          <ShieldCheck className="h-3.5 w-3.5" /> Identity verified
        </p>
      )}
      <Link to="/profile/edit" className="block mt-4">
        <Button className="btn-primary h-10 px-5 text-sm" data-testid="edit-profile-btn">
          <Edit className="h-4 w-4 mr-1.5" /> Edit profile
        </Button>
      </Link>
    </CardContent>
  </Card>
);

const AboutMeSection = ({ u, prompts, onAddBio }) => {
  const hasAnything = u.tagline || u.long_bio || prompts.length > 0;
  if (!hasAnything) {
    return (
      <Card className="bg-amber-50 border-amber-200/60" data-testid="profile-empty-nudge">
        <CardContent className="p-5 sm:p-6 text-center">
          <div className="mx-auto w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-3">
            <Sparkles className="h-5 w-5 text-amber-600" />
          </div>
          <h3 className="font-heading font-bold text-base text-slate-900">
            Tell landlords about you.
          </h3>
          <p className="mt-1 text-sm text-slate-700 max-w-sm mx-auto">
            A one-line tagline and a short bio more than double the reply rate in early testing.
          </p>
          <Button
            className="btn-primary mt-4 h-10 px-5"
            onClick={onAddBio}
            data-testid="profile-add-bio-cta"
          >
            Add about me
          </Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="bg-white border-slate-200/70 shadow-soft" data-testid="about-me-section">
      <CardContent className="p-5 sm:p-6">
        <h3 className="font-heading font-bold text-sm text-slate-900 uppercase tracking-wider mb-3">
          About me
        </h3>
        {u.tagline && (
          <blockquote className="border-l-4 border-amber-400 pl-4 py-1 italic text-base text-slate-800 font-medium leading-relaxed">
            "{u.tagline}"
          </blockquote>
        )}
        {u.long_bio && (
          <p className={`text-sm text-slate-700 leading-relaxed whitespace-pre-wrap ${u.tagline ? 'mt-4' : ''}`}>
            {u.long_bio}
          </p>
        )}
        {prompts.length > 0 && (
          <dl className={`grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4 ${(u.tagline || u.long_bio) ? 'mt-5 pt-5 border-t border-slate-100' : ''}`}>
            {prompts.map((p) => (
              <div key={p.key} data-testid={`prompt-${p.key}`}>
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                  {p.label}
                </dt>
                <dd className="mt-1 text-sm text-slate-700 leading-relaxed">{p.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
};

const DetailsSection = ({ u, languagesDisplay }) => {
  const hasDetails =
    u.school_name || u.year_of_study || u.program_of_study ||
    languagesDisplay.length > 0 || u.expected_move_in_date;
  if (!hasDetails) return null;
  return (
    <Card className="bg-white border-slate-200/70 shadow-soft" data-testid="details-section">
      <CardContent className="p-5 sm:p-6 space-y-3">
        <h3 className="font-heading font-bold text-sm text-slate-900 uppercase tracking-wider">
          Details
        </h3>
        {(u.school_name || u.year_of_study || u.program_of_study) && (
          <p className="text-sm text-slate-700 flex items-start gap-2">
            <GraduationCap className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <span>
              {u.year_of_study && <>{YEAR_LABELS[u.year_of_study]}</>}
              {u.school_name && <> at <span className="font-medium text-slate-900">{u.school_name}</span></>}
              {u.program_of_study && <>, {u.program_of_study}</>}
            </span>
          </p>
        )}
        {languagesDisplay.length > 0 && (
          <div className="flex items-start gap-2">
            <Languages className="h-4 w-4 text-slate-400 mt-1 flex-shrink-0" />
            <div className="flex flex-wrap gap-1.5">
              {languagesDisplay.map((lang) => (
                <span
                  key={lang}
                  className="px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-full"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        )}
        {u.expected_move_in_date && (
          <p className="text-sm text-slate-700 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400 flex-shrink-0" />
            Looking to move in by {u.expected_move_in_date}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const TrustScoreSection = ({ score }) => {
  const navigate = useNavigate();
  return (
    <Card
      className="bg-white border-slate-200/70 shadow-soft"
      data-testid="trust-score-section"
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-4">
          <TrustScoreRing
            score={score.score}
            tier={score.tier}
            size={84}
            strokeWidth={8}
            showLabel={false}
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-sm text-slate-900 uppercase tracking-wider">
              Trust Score
            </h3>
            <p className="text-xl font-heading font-extrabold text-slate-900 leading-tight">
              <span data-testid="trust-score-value">{score.score}</span>
              <span className="text-sm font-normal text-slate-400"> / 100</span>
            </p>
            <p className="text-xs text-slate-700 font-medium mt-0.5">
              {TIER_LABELS[score.tier] || score.tier}
            </p>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Higher Trust Scores get faster landlord replies. It's a Sturent signal — not a credit score.
        </p>
        <ul className="mt-4 divide-y divide-slate-100" data-testid="trust-breakdown-list">
          {(score.components || []).map((c) => {
            const action = TRUST_ACTION[c.key];
            const clickable = !c.present && action && !action.disabled;
            const Row = clickable ? 'button' : 'div';
            return (
              <Row
                key={c.key}
                onClick={clickable ? () => navigate(action.to) : undefined}
                type={clickable ? 'button' : undefined}
                className={`w-full flex items-center gap-3 py-2.5 text-left transition-colors ${
                  clickable ? 'hover:bg-amber-50 -mx-2 px-2 rounded-lg cursor-pointer' : ''
                }`}
                data-testid={`trust-row-${c.key}`}
              >
                {c.present ? (
                  <span className="h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-emerald-700" strokeWidth={3} />
                  </span>
                ) : (
                  <Circle className="h-5 w-5 text-slate-300 flex-shrink-0" strokeWidth={1.5} />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${c.present ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>
                    {c.label}
                  </p>
                  {!c.present && action && (
                    <p className="text-[11px] text-amber-700 font-medium">
                      {action.disabled ? action.label : `${action.label} →`}
                    </p>
                  )}
                </div>
                <span className="text-xs text-slate-400 font-medium flex-shrink-0">+{c.weight}</span>
              </Row>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

const ReviewsPlaceholder = () => (
  <Card className="bg-slate-50 border-slate-200" data-testid="reviews-placeholder">
    <CardContent className="p-5 text-center">
      <p className="text-sm text-slate-600 leading-relaxed">
        Reviews from your landlords will appear here after you've completed a stay.
      </p>
    </CardContent>
  </Card>
);

/* ─────────────────────── Applications + Roommates tabs ─────────────────────── */

const ApplicationsTab = () => {
  const navigate = useNavigate();
  return (
    <Card className="bg-card-cream border-amber-200/60 shadow-soft" data-testid="applications-empty">
      <CardContent className="p-8 sm:p-10 text-center">
        <div className="mx-auto w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-soft">
          <FileCheck2 className="h-7 w-7 text-amber-600" />
        </div>
        <h2 className="text-xl font-heading font-extrabold text-slate-900">No applications yet.</h2>
        <p className="mt-2 text-sm text-slate-700 max-w-md mx-auto leading-relaxed">
          When you apply for a place, you'll see the application status, messages with the landlord,
          and lease details here.
        </p>
        <Button
          className="btn-primary mt-5 h-11 px-6"
          onClick={() => navigate('/search')}
          data-testid="applications-find-btn"
        >
          <SearchIcon className="h-4 w-4 mr-1.5" /> Find a place
        </Button>
      </CardContent>
    </Card>
  );
};

const RoommatesTab = () => {
  const navigate = useNavigate();
  return (
    <Card className="bg-card-cream border-amber-200/60 shadow-soft" data-testid="roommates-empty">
      <CardContent className="p-8 sm:p-10 text-center">
        <div className="mx-auto w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-soft">
          <UserPlus className="h-7 w-7 text-amber-600" />
        </div>
        <h2 className="text-xl font-heading font-extrabold text-slate-900">
          Roommate matching, coming soon.
        </h2>
        <p className="mt-2 text-sm text-slate-700 max-w-md mx-auto leading-relaxed">
          We're building an AI-powered roommate matcher that finds compatible students based on
          lifestyle, sleep schedule, and study habits. Make sure your lifestyle preferences are filled
          in so you'll be ready when it launches.
        </p>
        <Button
          className="btn-primary mt-5 h-11 px-6"
          onClick={() => navigate('/profile/edit#lifestyle')}
          data-testid="roommates-lifestyle-btn"
        >
          Update lifestyle preferences
        </Button>
      </CardContent>
    </Card>
  );
};

/* ──────────────────────────── Page shell + tabs ──────────────────────────── */

const ProfileMe = () => {
  const navigate = useNavigate();
  const { bundle, loading } = useProfile();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'about');

  const changeTab = (key) => {
    setTab(key);
    const next = new URLSearchParams(params);
    if (key === 'about') next.delete('tab'); else next.set('tab', key);
    setParams(next, { replace: true });
  };

  if (loading || !bundle) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  const u = bundle.user;
  const score = bundle.trust_score;
  const photoUrl = bundle.profile_photo_token
    ? `${API}/api/files/${bundle.profile_photo_token}`
    : null;
  const initial = (u.first_name || u.email || '?').charAt(0).toUpperCase();
  const identityVerified = !!(u.email_verified && u.phone_verified);

  const prompts = Object.entries(PROMPT_LABELS)
    .map(([k, label]) => ({ key: k, label, value: u[k] }))
    .filter((p) => p.value);
  const languagesDisplay = (() => {
    const base = u.languages || [];
    return (u.languages_other ? [...base, u.languages_other] : base).filter(Boolean);
  })();

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="profile-me-page">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
        {/* Page header + mobile tab strip */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-slate-900">
            My profile
          </h1>
        </div>

        {/* Mobile top tabs */}
        <div
          className="md:hidden flex gap-1 mb-4 overflow-x-auto scrollbar-thin -mx-4 px-4"
          data-testid="profile-mobile-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => changeTab(t.key)}
              className={`flex-shrink-0 px-3.5 py-2 rounded-full text-xs font-semibold transition-colors ${
                tab === t.key
                  ? 'bg-amber-500 text-white'
                  : 'bg-white border border-slate-200 text-slate-700'
              }`}
              data-testid={`tab-mobile-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {/* Desktop left rail */}
          <aside className="hidden md:block md:col-span-1" data-testid="profile-left-rail">
            <nav className="sticky top-24 space-y-1">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => changeTab(t.key)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                      active
                        ? 'bg-amber-50 text-amber-800 font-semibold'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`tab-desktop-${t.key}`}
                  >
                    <Icon className={`h-4 w-4 ${active ? 'text-amber-600' : 'text-slate-400'}`} />
                    {t.label}
                    {active && <ChevronRight className="ml-auto h-4 w-4 text-amber-400" />}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content area */}
          <div className="md:col-span-3">
            {tab === 'about' && (
              <div
                className="grid grid-cols-1 md:grid-cols-5 gap-4"
                data-testid="about-tab-content"
              >
                <div className="md:col-span-2">
                  <IdentityCard
                    u={u}
                    photoUrl={photoUrl}
                    initial={initial}
                    identityVerified={identityVerified}
                  />
                </div>
                <div className="md:col-span-3 space-y-4">
                  <AboutMeSection
                    u={u}
                    prompts={prompts}
                    onAddBio={() => navigate('/profile/edit#about')}
                  />
                  <DetailsSection u={u} languagesDisplay={languagesDisplay} />
                  <TrustScoreSection score={score} />
                  {/* Reviews placeholder hidden pre-launch (everyone has 0 stays) */}
                  {false && <ReviewsPlaceholder />}
                </div>
              </div>
            )}

            {tab === 'applications' && <ApplicationsTab />}
            {tab === 'roommates' && <RoommatesTab />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileMe;
